import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import healthApi from '@/api/healthApi';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Activity, TrendingUp, TrendingDown, Minus, ArrowLeft, Calendar, Target } from 'lucide-react';
import { toast } from 'sonner';
import MedicalDisclaimerBanner from '@/components/MedicalDisclaimerBanner';
import ContentCategoryLabel from '@/components/ContentCategoryLabel';

const HealthInsightsDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTrends();
  }, []);

  const fetchTrends = async () => {
    try {
      const { data } = await healthApi.getCheckInTrends(14);
      setTrends(data);
    } catch (error) {
      console.error('Error fetching trends:', error);
      toast.error('Could not load health trends');
    } finally {
      setLoading(false);
    }
  };

  const getTrendIcon = (trend) => {
    if (trend === 'improving') return <TrendingUp className="h-5 w-5 text-green-600" />;
    if (trend === 'declining') return <TrendingDown className="h-5 w-5 text-red-600" />;
    return <Minus className="h-5 w-5 text-gray-600" />;
  };

  const getTrendColor = (trend) => {
    if (trend === 'improving') return 'text-green-700 bg-green-50 border-green-200';
    if (trend === 'declining') return 'text-red-700 bg-red-50 border-red-200';
    return 'text-gray-700 bg-gray-50 border-gray-200';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <MedicalDisclaimerBanner page="insights" />
      <div className="p-4">
        <div className="max-w-7xl mx-auto pt-4 pb-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Home
        </Button>

        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Health Intelligence Dashboard 🧠
            </h1>
            <ContentCategoryLabel category="EDUCATION" size="sm" />
          </div>
          <p className="text-gray-600">
            Comprehensive insights from your past 14 days of check-ins
          </p>
        </div>

        {trends && trends.total_checkins > 0 ? (
          <div className="space-y-6">
            {/* Stats Overview */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 font-medium">Check-in Consistency</p>
                      <p className="text-3xl font-bold text-indigo-600 mt-1">
                        {trends.patterns.consistency_score}%
                      </p>
                    </div>
                    <Calendar className="h-12 w-12 text-indigo-200" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 font-medium">Avg Sleep Quality</p>
                      <p className="text-3xl font-bold text-purple-600 mt-1">
                        {trends.patterns.avg_sleep}/10
                      </p>
                    </div>
                    <Activity className="h-12 w-12 text-purple-200" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 font-medium">Avg Energy Level</p>
                      <p className="text-3xl font-bold text-orange-600 mt-1">
                        {trends.patterns.avg_energy}/10
                      </p>
                    </div>
                    <Target className="h-12 w-12 text-orange-200" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Trend Patterns */}
            <Card className="border-2 border-indigo-200">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <TrendingUp className="h-6 w-6 mr-2 text-indigo-600" />
                  Your Health Patterns
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className={`p-4 rounded-lg border-2 ${getTrendColor(trends.patterns.sleep_trend)}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold">Sleep Trend</span>
                      {getTrendIcon(trends.patterns.sleep_trend)}
                    </div>
                    <p className="text-sm capitalize">{trends.patterns.sleep_trend.replace('_', ' ')}</p>
                  </div>

                  <div className={`p-4 rounded-lg border-2 ${getTrendColor(trends.patterns.energy_trend)}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold">Energy Trend</span>
                      {getTrendIcon(trends.patterns.energy_trend)}
                    </div>
                    <p className="text-sm capitalize">{trends.patterns.energy_trend.replace('_', ' ')}</p>
                  </div>

                  <div className={`p-4 rounded-lg border-2 ${trends.patterns.stress_pattern === 'concerning' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-700'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold">Stress Pattern</span>
                      <Activity className="h-5 w-5" />
                    </div>
                    <p className="text-sm capitalize">{trends.patterns.stress_pattern}</p>
                  </div>

                  <div className="p-4 rounded-lg border-2 bg-blue-50 border-blue-200 text-blue-700">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold">Total Check-Ins</span>
                      <Calendar className="h-5 w-5" />
                    </div>
                    <p className="text-sm">{trends.total_checkins} in past 14 days</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Insights */}
            {trends.insights && trends.insights.length > 0 && (
              <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50">
                <CardHeader>
                  <CardTitle className="flex items-center text-purple-900">
                    <Activity className="h-6 w-6 mr-2" />
                    Personalized Insights
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {trends.insights.map((insight, index) => (
                      <div key={index} className="flex items-start gap-3 p-3 bg-white rounded-lg border border-purple-200">
                        <span className="text-lg flex-shrink-0 mt-0.5">💡</span>
                        <p className="text-sm text-gray-800">{insight}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* CTA */}
            <Card className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white border-0">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold mb-2">Continue Your Health Journey</h3>
                <p className="mb-4 text-indigo-100">
                  Complete your daily check-in to keep tracking your progress and receive personalized insights.
                </p>
                <Button 
                  onClick={() => navigate('/check-in')}
                  variant="secondary"
                  className="bg-white text-indigo-600 hover:bg-indigo-50"
                >
                  Complete Today's Check-In
                </Button>
              </CardContent>
            </Card>
          </div>
        ) : (
          <Card className="border-2 border-indigo-200">
            <CardContent className="p-12 text-center">
              <Activity className="h-16 w-16 text-indigo-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Start Your Health Intelligence Journey
              </h3>
              <p className="text-gray-600 mb-6">
                Complete at least 3 check-ins to unlock personalized health insights and trend analysis.
              </p>
              <Button 
                onClick={() => navigate('/check-in')}
                className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
              >
                Complete Your First Check-In
              </Button>
            </CardContent>
          </Card>
        )}
        </div>
      </div>
    </div>
  );
};

export default HealthInsightsDashboard;
